// Cliente do abastecimento: toda confirmação é online e a autoridade de saldo,
// destino e idempotência continua no servidor.
import { apiFetch, apiMutate } from '../csrf-fetch';
import type {
  ConfirmarEntregaAbastecimentoInput,
  FiltroFilaAbastecimento,
  ListaFilaAbastecimento,
  SepararSolicitacaoAbastecimentoInput,
  SolicitacaoAbastecimentoDetalheView,
  SolicitarAbastecimentoView,
  SolicitarParaLoteInput,
  SolicitarParaSetorInput,
} from '@pieltec/contracts';

export interface MaterialLiberacaoView {
  readonly id: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly quantidade: string;
  readonly unidade: string;
}

export interface FaltaLiberacaoView {
  readonly material: string;
  readonly quantidade: string;
  readonly unidade: string;
}

export interface PreflightLiberacao {
  readonly versao: number;
  readonly revisao: { readonly codigo: string | null; readonly desenhoObrigatorio: boolean; readonly desenhoDisponivel: boolean };
  readonly materiais: readonly MaterialLiberacaoView[];
  readonly faltas: readonly FaltaLiberacaoView[];
  readonly estruturaAusente: boolean;
  /** Fase 17.3 (D-12): avisos do consumo previsto de tinta; nunca bloqueiam a liberação. */
  readonly avisosConsumo?: readonly string[];
}

export type ResultadoLiberacao =
  | { readonly situacao: 'LIBERADA'; readonly solicitacaoId: string | null; readonly op: { readonly id: string; readonly status: string } }
  | { readonly situacao: 'REQUER_CIENCIA_ESTRUTURA'; readonly opId: string; readonly versao: number }
  | { readonly situacao: 'REQUER_CIENCIA_FALTA'; readonly opId: string; readonly versao: number; readonly alertas: readonly { readonly codigo: string; readonly deficit: string; readonly unidade: string }[] }
  | { readonly situacao: 'BLOQUEADA_DESENHO' };

/**
 * `codigo` é o código de negócio do corpo de erro (ex.: `SALDO_INSUFICIENTE`), quando o servidor o envia;
 * permite distinguir uma recusa de negócio de um conflito de concorrência que também chega como 409.
 */
export class ErroAbastecimento extends Error {
  constructor(readonly status: number | null, message: string, readonly codigo: string | null = null) { super(message); }
}

async function corpo<T>(resposta: Response, padrao: string): Promise<T> {
  if (resposta.ok) return resposta.json() as Promise<T>;
  const dados = (await resposta.json().catch(() => null)) as { message?: unknown; codigo?: unknown } | null;
  const mensagem = typeof dados?.message === 'string' ? dados.message : padrao;
  throw new ErroAbastecimento(resposta.status, mensagem, typeof dados?.codigo === 'string' ? dados.codigo : null);
}

async function mutar<T>(caminho: string, body: unknown, padrao: string): Promise<T> {
  try {
    return await corpo<T>(await apiMutate(caminho, { method: 'POST', body }), padrao);
  } catch (causa) {
    if (causa instanceof ErroAbastecimento) throw causa;
    throw new ErroAbastecimento(null, padrao);
  }
}

export const abastecimentoApi = {
  preflightLiberacao: (opId: string): Promise<PreflightLiberacao> => apiFetch<PreflightLiberacao>(`/api/v1/producao/ops/${encodeURIComponent(opId)}/liberacao`),
  liberar: (opId: string, ciencia?: 'FALTA_MATERIAL' | 'MATERIAIS_A_DEFINIR'): Promise<ResultadoLiberacao> =>
    mutar<ResultadoLiberacao>(`/api/v1/producao/ops/${encodeURIComponent(opId)}/liberar`, ciencia ? { ciencia } : {}, 'Não foi possível liberar a OP.'),
  solicitarLote: (input: SolicitarParaLoteInput): Promise<SolicitarAbastecimentoView> => mutar('/api/v1/estoque/abastecimento/lote', input, 'Não foi possível registrar a solicitação.'),
  solicitarSetor: (input: SolicitarParaSetorInput): Promise<SolicitarAbastecimentoView> => mutar('/api/v1/estoque/abastecimento/setor', input, 'Não foi possível registrar a solicitação.'),
  listarFila: (filtro: FiltroFilaAbastecimento, opcoes: { readonly signal?: AbortSignal } = {}): Promise<ListaFilaAbastecimento> => {
    const busca = new URLSearchParams();
    if (filtro.pagina !== undefined) busca.set('pagina', String(filtro.pagina));
    if (filtro.tamanhoPagina !== undefined) busca.set('tamanhoPagina', String(filtro.tamanhoPagina));
    for (const chave of ['estado', 'situacao', 'setor', 'loteOuOp', 'material', 'de', 'ate', 'ordenacao'] as const) {
      const valor = filtro[chave];
      if (valor !== undefined && valor.trim() !== '') busca.set(chave, valor.trim());
    }
    return apiFetch<ListaFilaAbastecimento>(`/api/v1/estoque/abastecimento/fila?${busca.toString()}`, { signal: opcoes.signal });
  },
  /** Com `itemId`, cabeçalho, fontes e entregas ficam restritos ao item (linha) aberto na fila. */
  detalhe: (id: string, itemId?: string): Promise<SolicitacaoAbastecimentoDetalheView> =>
    apiFetch<SolicitacaoAbastecimentoDetalheView>(`/api/v1/estoque/abastecimento/${encodeURIComponent(id)}${itemId ? `?item=${encodeURIComponent(itemId)}` : ''}`),
  separar: (id: string, input: SepararSolicitacaoAbastecimentoInput): Promise<SolicitarAbastecimentoView> => mutar(`/api/v1/estoque/abastecimento/${encodeURIComponent(id)}/separacoes`, input, 'Não foi possível registrar a separação.'),
  entregar: (id: string, input: ConfirmarEntregaAbastecimentoInput) => mutar(`/api/v1/estoque/abastecimento/${encodeURIComponent(id)}/entregas`, input, 'Não foi possível confirmar a entrega.'),
};
